import {
  faChevronDown,
  faFileExcel,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { colors } from "../../../global";

const SheetInformation = ({
  sheetList,
  metadata,
  handleSheetChange,
  handleSheetNameChange,
  handleOverwriteSheet,
  openExcelModal,
  excelSelectableFields,
}) => {
  const fields = [
    "name",
    "service_filter",
    "service_filter_column",
    "first_data_row",
    "last_data_row",
    "buffer_percentages",
    "margin_percentages",
  ];
  // Fields that support Excel range selection
  const formatLabel = (key) => {
    return key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold" style={{ color: colors.primary }}>
          Sheet Information
        </h3>
        <div className="flex items-center gap-2">
          <FontAwesomeIcon
            icon={faFileExcel}
            size={16}
            style={{ color: colors.darkGray }}
          />
          <span className="text-sm" style={{ color: colors.darkGray }}>
            Configuration
          </span>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
        {/* Sheet Selection */}
        {sheetList.length > 0 && (
          <div className="space-y-2">
            <label
              className="text-sm font-medium"
              style={{ color: colors.darkGray }}
            >
              Sheet Name
            </label>
            <div className="relative">
              <select
                className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 appearance-none bg-white pr-10"
                style={{
                  "--tw-ring-color": colors.primary + "40",
                  borderColor: metadata.sheet_information.sheet_id
                    ? colors.primary + "40"
                    : undefined,
                }}
                value={metadata.sheet_information.sheet_id || ""}
                onChange={(e) => {
                  const selectedId = e.target.value;
                  const selectedSheet = sheetList.find(
                    (sheet) => sheet.id.toString() === selectedId.toString()
                  );
                  if (selectedSheet) {
                    handleSheetNameChange(
                      selectedId,
                      selectedSheet.information.name
                    );
                  }
                }}
              >
                <option disabled value="" className="text-gray-400">
                  Select Sheet Name
                </option>
                {sheetList.map((sheet, index) => (
                  <option
                    key={index}
                    value={sheet.information.id}
                    className="py-2"
                  >
                    {sheet.information.name}
                  </option>
                ))}
              </select>
              <FontAwesomeIcon
                icon={faChevronDown}
                size={16}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
                style={{ color: colors.darkGray }}
              />
            </div>
          </div>
        )}

        {/* Configuration Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {fields.map((key) => {
            const isExcelSelectable = excelSelectableFields.includes(key);

            return (
              <div key={key} className="space-y-2">
                <label
                  className="text-sm font-medium"
                  style={{ color: colors.darkGray }}
                >
                  {formatLabel(key)}
                </label>

                {isExcelSelectable ? (
                  <div className="flex">
                    <input
                      type="text"
                      placeholder={`Enter ${formatLabel(key).toLowerCase()}`}
                      className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-l-lg focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200"
                      style={{
                        "--tw-ring-color": colors.primary + "40",
                        borderColor: metadata.sheet_information[key]
                          ? colors.primary + "40"
                          : undefined,
                      }}
                      value={metadata.sheet_information[key] ?? ""}
                      onChange={(e) => handleSheetChange(key, e.target.value)}
                    />
                    {openExcelModal &&
                      (metadata.file_name.endsWith(".csv") ||
                        metadata?.sheet_information?.sheet_name) && (
                        <button
                          onClick={() => openExcelModal(key)}
                          type="button"
                          aria-label={`Open Excel Sheet to select ${formatLabel(
                            key
                          )}`}
                          className="px-3 py-2 border border-l-0 border-gray-200 rounded-r-lg hover:bg-gray-50 transition-colors duration-200 flex items-center justify-center"
                          style={{ color: colors.primary }}
                        >
                          <FontAwesomeIcon icon={faFileExcel} size={16} />
                        </button>
                      )}
                  </div>
                ) : (
                  <input
                    type="text"
                    placeholder={`Enter ${formatLabel(key).toLowerCase()}`}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200"
                    style={{
                      "--tw-ring-color": colors.primary + "40",
                      borderColor: metadata.sheet_information[key]
                        ? colors.primary + "40"
                        : undefined,
                    }}
                    value={metadata.sheet_information[key] ?? ""}
                    onChange={(e) => handleSheetChange(key, e.target.value)}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Override Toggle */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div className="space-y-1">
            <label
              className="text-sm font-medium"
              style={{ color: colors.darkGray }}
            >
              Override Current Sheet
            </label>
            <p className="text-xs" style={{ color: colors.darkGray + "80" }}>
              Replace existing sheet data with new configuration
            </p>
          </div>

          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={metadata.sheet_information.overwrite_sheet || false}
              onChange={(e) => handleOverwriteSheet(e.target.checked)}
              className="sr-only peer"
            />
            <div
              className="relative w-11 h-6 rounded-full transition-colors duration-200 ease-in-out peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-offset-2 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all after:duration-200 after:shadow-sm"
              style={{
                backgroundColor: metadata.sheet_information.overwrite_sheet
                  ? colors.primary
                  : "#d1d5db",
                "--tw-ring-color": colors.primary + "40",
              }}
            />
          </label>
        </div>

        {/* Info Banner */}
        <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div
            className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
            style={{ backgroundColor: colors.primary }}
          >
            <span className="text-white text-xs font-bold">i</span>
          </div>
          <div className="space-y-1">
            <p
              className="text-sm font-medium"
              style={{ color: colors.primary }}
            >
              Configuration Guide
            </p>
            <p className="text-xs" style={{ color: colors.darkGray }}>
              Use the Excel button next to fields to select cells directly from
              your spreadsheet. This ensures accurate column and row references.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SheetInformation;
